// client/src/pages/secure/IntegrationsPage.js — Administration -> Integrations.
//
// The managing broker sets the credentials the portal uses for Claude (website chat and
// agent assistant), Twilio texting, the MLS listing feed and the Do Not Call registry.
// A value saved here wins over the server's .env file; clearing it falls back to the
// file again. The server never sends a whole secret back — only its last four characters.
// Microsoft 365 is shown for information only.
import React, { useCallback, useEffect, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import { getCurrentUser } from '../../hooks/useCurrentUser';
import PageHeader, { AboutThisPage } from '../../components/shared/PageHeader';
import DataList from '../../components/shared/DataList';

const SOURCE_LABEL = {
    portal: 'Set here in the portal',
    server: 'Set in the server file',
};

const Badge = ({ good, children }) => (
    <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${good ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
        {children}
    </span>
);

const fmt = (d) => (d ? new Date(d).toLocaleString() : '');

const FieldRow = ({ field, readOnly, draft, onDraft, clearing, onToggleClear }) => {
    const status = field.configured
        ? `${SOURCE_LABEL[field.source] || 'Set'}${field.display ? `: ${field.display}` : ''}`
        : 'Not set';
    return (
        <div className="py-3 border-t border-slate-100 first:border-t-0">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
                <label htmlFor={`f-${field.key}`} className="text-sm font-medium text-slate-700">{field.label}</label>
                <span className={`text-xs break-all ${field.configured ? 'text-slate-600' : 'text-slate-400'}`}>{status}</span>
            </div>
            {field.help && <p className="text-xs text-slate-500 mt-0.5">{field.help}</p>}
            {field.portal_unreadable && (
                <p className="text-xs text-red-600 mt-1">A value saved here cannot be read on this server (its encryption key changed). Enter it again.</p>
            )}
            {field.source === 'portal' && field.server_value_present && (
                <p className="text-xs text-slate-500 mt-0.5">The server file also has a value; the portal value is the one in use.</p>
            )}
            {field.updated_at && (
                <p className="text-xs text-slate-400 mt-0.5">Last changed {fmt(field.updated_at)}{field.updated_by ? ` by ${field.updated_by}` : ''}</p>
            )}
            {!readOnly && (
                <div className="mt-2 flex flex-col sm:flex-row sm:items-center gap-2">
                    {field.choices ? (
                        <select
                            id={`f-${field.key}`}
                            value={draft || ''}
                            disabled={clearing}
                            onChange={(e) => onDraft(field.key, e.target.value)}
                            className="block w-full sm:max-w-xs p-2 border border-gray-300 rounded-md text-sm"
                        >
                            <option value="">— leave as is —</option>
                            {field.choices.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                    ) : (
                        <input
                            id={`f-${field.key}`}
                            type={field.secret ? 'password' : 'text'}
                            autoComplete="off"
                            value={draft || ''}
                            disabled={clearing}
                            onChange={(e) => onDraft(field.key, e.target.value)}
                            placeholder={field.configured ? 'Type a new value to replace it' : 'Enter value'}
                            className="block w-full p-2 border border-gray-300 rounded-md text-sm"
                        />
                    )}
                    {field.source === 'portal' && (
                        <label className="inline-flex items-center gap-1 text-xs text-slate-600 whitespace-nowrap">
                            <input type="checkbox" checked={!!clearing} onChange={() => onToggleClear(field.key)} />
                            Clear portal value{field.server_value_present ? ' (use server file)' : ''}
                        </label>
                    )}
                </div>
            )}
        </div>
    );
};

const IntegrationCard = ({ integ, storageOk, onSaved }) => {
    const apiClient = useApiClient();
    const [drafts, setDrafts] = useState({});
    const [clears, setClears] = useState({});
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [message, setMessage] = useState(null);       // { kind: 'ok'|'error'|'info', text }

    const dirty = Object.values(drafts).some((v) => String(v || '').trim()) || Object.values(clears).some(Boolean);

    const save = async () => {
        setSaving(true); setMessage(null);
        try {
            const values = Object.fromEntries(Object.entries(drafts).filter(([, v]) => String(v || '').trim()));
            const clear = Object.keys(clears).filter((k) => clears[k]);
            const r = await apiClient.put(`/api/integrations/${integ.id}`, { values, clear });
            setDrafts({}); setClears({});
            setMessage({ kind: 'ok', text: `Saved (${r.changed.map((c) => `${c.key} ${c.action}`).join(', ') || 'no change'}). It takes effect within a few seconds.` });
            onSaved(r);
        } catch (e) {
            setMessage({ kind: 'error', text: e.message || 'Could not save.' });
        } finally { setSaving(false); }
    };

    const test = async () => {
        setTesting(true); setMessage(null);
        try {
            const r = await apiClient.post(`/api/integrations/${integ.id}/test`, {});
            setMessage({ kind: r.ok === true ? 'ok' : (r.ok === false ? 'error' : 'info'), text: r.message });
            onSaved(null);
        } catch (e) {
            setMessage({ kind: 'error', text: e.message || 'Test failed.' });
        } finally { setTesting(false); }
    };

    const msgClass = { ok: 'text-green-700 bg-green-50', error: 'text-red-700 bg-red-50', info: 'text-slate-700 bg-slate-50' };

    return (
        <section className="bg-white rounded-lg shadow-md p-5 sm:p-6">
            <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                    <h2 className="text-lg font-semibold text-slate-800">{integ.title}</h2>
                    <p className="text-sm text-slate-600 mt-0.5">{integ.purpose}</p>
                </div>
                <Badge good={integ.configured}>{integ.configured ? 'Configured' : 'Not configured'}</Badge>
            </div>
            {!integ.configured && integ.missing.length > 0 && (
                <p className="text-xs text-amber-700 mt-2">Still needed: {integ.missing.join(', ')}.</p>
            )}
            {integ.read_only && (
                <p className="mt-3 text-sm rounded-md bg-slate-100 text-slate-700 px-3 py-2">
                    Read-only. {integ.read_only_note}
                </p>
            )}

            <div className="mt-3">
                {integ.fields.map((f) => (
                    <FieldRow
                        key={f.key}
                        field={f}
                        readOnly={integ.read_only || !storageOk}
                        draft={drafts[f.key]}
                        onDraft={(k, v) => setDrafts((d) => ({ ...d, [k]: v }))}
                        clearing={clears[f.key]}
                        onToggleClear={(k) => setClears((c) => ({ ...c, [k]: !c[k] }))}
                    />
                ))}
            </div>

            {message && <p className={`mt-3 text-sm rounded-md px-3 py-2 ${msgClass[message.kind]}`}>{message.text}</p>}

            {!integ.read_only && (
                <div className="mt-4 flex flex-wrap gap-2 justify-end">
                    {integ.testable ? (
                        <button type="button" onClick={test} disabled={testing || saving}
                            className="px-4 py-2 border border-slate-300 rounded-md text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                            {testing ? 'Testing…' : 'Test connection'}
                        </button>
                    ) : (
                        integ.test_note && <p className="text-xs text-slate-500 flex-1">{integ.test_note}</p>
                    )}
                    {storageOk && (
                        <button type="button" onClick={save} disabled={!dirty || saving}
                            className="px-4 py-2 bg-slate-800 text-white rounded-md text-sm hover:bg-slate-900 disabled:bg-indigo-300">
                            {saving ? 'Saving…' : 'Save changes'}
                        </button>
                    )}
                </div>
            )}
        </section>
    );
};

const ACTION_LABEL = { set: 'set', replaced: 'replaced', cleared: 'cleared', tested: 'tested' };

const IntegrationsPage = () => {
    const apiClient = useApiClient();
    const user = getCurrentUser();
    const [data, setData] = useState(null);
    const [error, setError] = useState('');

    const load = useCallback(() => {
        apiClient.get('/api/integrations')
            .then((d) => { setData(d); setError(''); })
            .catch((e) => setError(e.message || 'Could not load integrations.'));
    }, [apiClient]);

    useEffect(() => { load(); }, [load]);

    if (!user?.isManagingBroker) {
        return <div className="p-8 text-slate-600">Integrations are managed by the managing broker.</div>;
    }
    if (error && !data) return <div className="p-8 text-red-600">{error}</div>;
    if (!data) return <div className="p-8">Loading integrations…</div>;

    const titleOf = Object.fromEntries(data.integrations.map((i) => [i.id, i.title]));
    const onSaved = (r) => { if (r && r.integrations) setData((d) => ({ ...d, ...r })); else load(); };

    return (
        <div className="space-y-6 max-w-4xl">
            <div>
                <PageHeader title="Integrations" description="The outside services the portal connects to." className="!mb-3" />
                <AboutThisPage className="!mb-0">
                    <p>A value entered here is stored encrypted and is used instead of the server file; clear it to go
                    back to the server file. Saved secrets are never shown again — only their last four characters.
                    "Test connection" only signs in and reads; it never sends a text, an email or a message.</p>
                </AboutThisPage>
            </div>

            {!data.storage.available && (
                <p className="rounded-md bg-amber-50 text-amber-800 px-4 py-3 text-sm">
                    {data.storage.message} Until this is fixed, the status below is shown but nothing can be saved here.
                </p>
            )}

            {data.integrations.map((i) => (
                <IntegrationCard key={i.id} integ={i} storageOk={data.storage.available} onSaved={onSaved} />
            ))}

            <section className="bg-white rounded-lg shadow-md p-5 sm:p-6">
                <h2 className="text-lg font-semibold text-slate-800">Recent changes and tests</h2>
                {(!data.events || data.events.length === 0) ? (
                    <p className="text-sm text-slate-500 mt-2">Nothing yet.</p>
                ) : (
                    <div className="overflow-x-auto mt-2">
                        <DataList rows={data.events} rowKey="id" csvName="integration-events" defaultSort={{ key: 'created_at', dir: 'desc' }}
                            filters={[{ key: 'integration', label: 'Integration' }, { key: 'user_name', label: 'Who' }]}
                            columns={[
                                { key: 'created_at', label: 'When', type: 'date', render: (e) => <span className="whitespace-nowrap">{fmt(e.created_at)}</span> },
                                { key: 'user_name', label: 'Who', render: (e) => e.user_name || '—' },
                                { key: 'integration', label: 'Integration', value: (e) => titleOf[e.integration] || e.integration },
                                { key: 'what', label: 'What', value: (e) => `${e.setting_key ? `${e.setting_key} ` : ''}${ACTION_LABEL[e.action] || e.action}${e.outcome ? ` — ${e.outcome}` : ''}${e.action === 'tested' && e.detail ? ` (${e.detail})` : ''}`,
                                    render: (e) => <>{e.setting_key ? `${e.setting_key} ` : ''}{ACTION_LABEL[e.action] || e.action}{e.outcome ? ` — ${e.outcome}` : ''}
                                        {e.action === 'tested' && e.detail ? <div className="text-xs text-slate-500">{e.detail}</div> : null}</> },
                            ]} />
                    </div>
                )}
            </section>
        </div>
    );
};

export default IntegrationsPage;
